import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import styles from './PanelNavigation.module.scss';

type PanelId = string | number | null;
export type PanelNavigationProps = {
  id: PanelId;
  direction: 'in' | 'out';
  children: () => ReactNode;
};

const PanelNavigation: FC<PanelNavigationProps> = ({ children, id, direction }) => {
  const [previousId, setPreviousId] = useState<PanelId>();
  const [content, setContent] = useState<ReactNode>(null);
  const previousRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [reveal, setReveal] = useState(false);
  const [html, setHtml] = useState('');
  const [allowUpdate, setAllowUpdate] = useState(true);
  const [previousScroll, setPreviousScroll] = useState(0);

  useEffect(() => {
    // Set scroll for each panel
    if (reveal && elementRef.current) {
      // Previous element has been replaced, scroll was reset
      previousRef.current?.scrollTo({ top: previousScroll, behavior: 'instant' });
      elementRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [previousScroll, reveal]);

  const updateElement = useCallback(() => {
    setReveal(true);
    setAllowUpdate(true);
  }, []);

  const stopReveal = useCallback(() => {
    setReveal(false);
    setHtml('');
  }, []);

  useEffect(() => {
    if (previousId === id) return;
    setAllowUpdate(false);

    if (elementRef.current) {
      setHtml(elementRef.current.innerHTML);
      setPreviousScroll(elementRef.current.scrollTop);
    }

    // prevent focus causing issues
    (document.activeElement as HTMLElement | null)?.blur();
    setPreviousId(id);
    updateElement();
  }, [children, id, previousId, updateElement]);

  useEffect(() => {
    if (allowUpdate) {
      setContent(children());
    }
  }, [allowUpdate, children]);

  return (
    <div
      className={styles.root}
      ref={rootRef}
    >
      <div
        className={getClassNames(styles, 'previous', classNames({
          out: reveal && direction === 'out',
          above: direction === 'out' && !!html.length,
        }))}
        // Duplicate the final state of the content. Re-rendering via React will not
        // show latest state.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
        onAnimationEnd={direction === 'out' ? stopReveal : undefined}
        ref={previousRef}
      />
      <div
        className={getClassNames(styles, 'current', classNames({
          in: reveal && direction === 'in',
          above: direction === 'in',
          reveal,
        }))}
        onAnimationEnd={direction === 'in' ? stopReveal : undefined}
        ref={elementRef}
      >
        {content}
      </div>
    </div>
  );
};

export default PanelNavigation;
